import { useState, type FormEvent } from 'react';
import {
  Anchor,
  Button,
  Container,
  Fieldset,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { errorMessage } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { ErrorNote } from '../components/ErrorNote';

/** Creates the publisher, its first brand and the owner account in one step. */
export function Signup() {
  const { status, signUp } = useAuth();
  const navigate = useNavigate();
  const [orgName, setOrgName] = useState('');
  const [brandName, setBrandName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (status === 'signed_in') return <Navigate to="/interactions" replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    setBusy(true);
    try {
      await signUp({
        orgName: orgName.trim(),
        brandName: brandName.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        password,
      });
      void navigate('/interactions', { replace: true });
    } catch (error) {
      setProblem(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Container component="main" size={460} pt={80} pb={64}>
      <Title order={1}>Set up your publisher account</Title>
      <Text size="lg" mt="sm" mb="lg">
        You can add more brands and colleagues later.
      </Text>
      {problem !== null && <ErrorNote message={problem} />}
      <form onSubmit={(event) => void submit(event)}>
        <Fieldset variant="unstyled" disabled={busy}>
          <Stack gap="md">
            <TextInput
              id="org-name"
              label="Publisher name"
              description="Your company or group, for example Northern News Group."
              required
              value={orgName}
              onChange={(event) => setOrgName(event.target.value)}
            />
            <TextInput
              id="brand-name"
              label="First brand"
              description="A title you publish, for example the Yorkshire Post."
              required
              value={brandName}
              onChange={(event) => setBrandName(event.target.value)}
            />
            {/*
             * Two fields rather than one, because they are stored as two and the API joins them
             * when a screen wants one name. A last name is optional: somebody who goes by a single
             * name should not have to invent a second one to open an account.
             */}
            <TextInput
              id="first-name"
              label="First name"
              autoComplete="given-name"
              required
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
            />
            <TextInput
              id="last-name"
              label="Last name"
              description="Optional."
              autoComplete="family-name"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
            />
            <TextInput
              id="signup-email"
              label="Email address"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <PasswordInput
              id="signup-password"
              label="Password"
              description="At least 12 characters."
              autoComplete="new-password"
              minLength={12}
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <Button type="submit" mt="xs">
              {busy ? 'Setting up...' : 'Create account'}
            </Button>
          </Stack>
        </Fieldset>
      </form>
      <Text c="dimmed" size="sm" mt="lg">
        Already have an account?{' '}
        <Anchor component={Link} to="/login" size="sm">
          Sign in
        </Anchor>
        .
      </Text>
    </Container>
  );
}
